import { getHostname, getMaybeAppName } from './utils';
import { safeLocalStorageGet } from './safeLocalStorage';
const PRIVATE_TOOLS_REGEXP = /^(tools|private)(?:-([a-z]+[0-9]))?.hubteam(qa)?\.com/i;
// foundations-components is storybook, which is an exception since we allow previewing components in different themes.
const SAFE_APPS = new Set(['foundations-editor-ui', 'hubbable-ui', 'hubbable-iframe-ui', 'foundations-components', 'trellis-design-docs', 'grove-ui', 'grove-sandbox-ui']);
export const THEME_DATA_ATTRIBUTE = 'data-hubspot-theme';
export const THEME_DATA_TAG = 'hubspotTheme';
export const LOCAL_STORAGE_THEME_KEY = 'trellis:theme';
export const LOCAL_STORAGE_THEME_VERSION_KEY = 'trellis:theme-version';
export const LOCAL_STORAGE_THEME_OVERRIDES_KEY = 'trellis:theme-overrides';
export const DEFAULT_SETTINGS = {
  themeName: 'canvas',
  themeMode: 'light',
  themeVersion: {
    type: 'default'
  },
  themeOverrides: {}
};
function shouldReadLocalThemeSettings() {
  try {
    // private/tools subdomain pages should not read theme from localStorage since not tokenized
    const hostname = getHostname();
    const maybeCurrentApp = getMaybeAppName();
    const isStorybook = window.HS_STORYBOOK !== undefined;
    const isThemableApp = !hostname.match(PRIVATE_TOOLS_REGEXP) || SAFE_APPS.has(maybeCurrentApp) || isStorybook;
    return isThemableApp;
  } catch (_unused) {
    // do nothing
  }
  return true;
}
function getThemeLocalStorageSetting(key) {
  if (!shouldReadLocalThemeSettings()) {
    return undefined;
  }
  return safeLocalStorageGet(key);
}
function splitChosenTheme(theme) {
  const [themeName, themeMode] = theme.split('-');
  if (!themeName || !themeMode) {
    return undefined;
  }
  return {
    themeName,
    themeMode
  };
}
function validateThemeVersion(themeVersion) {
  if (typeof themeVersion !== 'object' || !themeVersion.type) {
    return false;
  }
  if (themeVersion.type === 'deployed') {
    return typeof themeVersion.channel === 'string' && (!themeVersion.env || typeof themeVersion.env === 'string');
  }
  if (themeVersion.type === 'version') {
    return typeof themeVersion.version === 'string' && typeof themeVersion.channel === 'string';
  }
  if (themeVersion.type === 'local') {
    return typeof themeVersion.channel === 'string';
  }
  if (themeVersion.type === 'default') {
    return true;
  }
  return false;
}
function getThemeInfoFromHtml() {
  try {
    // dataset converts kebab to camel case
    const dataset = document.documentElement.dataset;
    if (!dataset) {
      return undefined;
    }
    const themeSetting = dataset[THEME_DATA_TAG];
    if (!themeSetting) {
      return undefined;
    }
    return splitChosenTheme(themeSetting);
  } catch (_unused2) {
    return undefined;
  }
}
function getChosenTheme() {
  const localThemeName = getThemeLocalStorageSetting(LOCAL_STORAGE_THEME_KEY);
  if (localThemeName) {
    const splitTheme = splitChosenTheme(localThemeName);
    if (splitTheme) {
      return splitTheme;
    }
  }
  const themeInfoFromHtml = getThemeInfoFromHtml();
  if (themeInfoFromHtml) {
    return themeInfoFromHtml;
  }
  return {
    themeName: DEFAULT_SETTINGS.themeName,
    themeMode: DEFAULT_SETTINGS.themeMode
  };
}
function getThemeVersion() {
  const localThemeVersion = getThemeLocalStorageSetting(LOCAL_STORAGE_THEME_VERSION_KEY);
  if (localThemeVersion) {
    try {
      const themeVersion = JSON.parse(localThemeVersion);
      if (validateThemeVersion(themeVersion)) {
        return themeVersion;
      }
    } catch (_unused3) {
      // do nothing
    }
  }
  return DEFAULT_SETTINGS.themeVersion;
}
function getThemeOverrides() {
  const localThemeSettings = getThemeLocalStorageSetting(LOCAL_STORAGE_THEME_OVERRIDES_KEY);
  if (localThemeSettings) {
    try {
      const themeOverrides = JSON.parse(localThemeSettings);
      if (typeof themeOverrides === 'object') {
        return themeOverrides;
      }
    } catch (_unused4) {
      // do nothing
    }
  }
  return {};
}
export function getLocalThemeSettings() {
  const chosenTheme = getChosenTheme();
  const themeVersion = getThemeVersion();
  const themeOverrides = getThemeOverrides();
  return Object.assign({
    themeOverrides,
    themeVersion
  }, chosenTheme);
}